import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import colors from "../../Constants/colors";
import { useFeatureCatalog } from "../../Query/useFeatureCatalog";
import {
  CatalogFeature,
  CatalogReleaseNote,
  FeatureAudience,
} from "../../Models/featureCatalog";
import { useLocation } from "react-router-dom";
import { ComoSelect, ComoSelectOption } from "../Common/ComoSelect";

// A feature counts as new when a visible release note touched it recently.
const NEW_DAYS = 30;

// Left bar and chip colour per change type, so the kind of change reads at a glance.
const CHANGE_TYPE_COLOR: Record<string, string> = {
  added: colors.green700,
  changed: colors.blue700,
  fixed: colors.orange600,
  removed: colors.red700,
};

type AudienceFilter = "all" | FeatureAudience;

const matchesSearch = (feature: CatalogFeature, search: string) => {
  if (!search) return true;
  const haystack = [
    feature.id,
    feature.name,
    feature.user.description,
    feature.technical?.description,
    ...(feature.technical?.frontend ?? []),
    ...(feature.technical?.backend ?? []),
    ...(feature.feature_flags ?? []),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(search.toLowerCase());
};

const newFeatureIds = (notes: CatalogReleaseNote[]) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - NEW_DAYS);
  const ids = new Set<string>();
  notes
    .filter((note) => note.audience !== "internal")
    .filter((note) => new Date(note.date) >= cutoff)
    .forEach((note) => note.feature_ids.forEach((id) => ids.add(id)));
  return ids;
};

const AnchorList = ({ label, items }: { label: string; items?: string[] }) => {
  if (!items?.length) return null;
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.gray700 }}>
        {label}
      </Typography>
      {items.map((item) => (
        <Typography
          key={item}
          sx={{
            fontSize: 12,
            fontFamily: "monospace",
            color: colors.gray600,
            wordBreak: "break-word",
          }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
};

const FeatureRow = ({
  feature,
  isNew,
}: {
  feature: CatalogFeature;
  isNew: boolean;
}) => {
  const { t } = useTranslation("featureCatalog");
  // Only admins get the technical part; without it the row does not open.
  const technical = feature.technical;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={technical ? undefined : false}
      sx={{
        mb: 1,
        bgcolor: colors.white,
        border: `1px solid ${colors.gray300}`,
        borderRadius: "10px !important",
        overflow: "hidden",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "&:before": { display: "none" },
        "&:hover": {
          borderColor: colors.accent,
          boxShadow: "0 1px 4px rgba(3, 46, 77, 0.08)",
        },
        "&.Mui-expanded": {
          borderColor: colors.accent,
          boxShadow: "0 2px 8px rgba(3, 46, 77, 0.10)",
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          technical && <ExpandMoreIcon sx={{ color: colors.primary_dark }} />
        }
        sx={{
          px: 2,
          cursor: technical ? "pointer" : "default",
          borderLeft: "4px solid transparent",
          transition: "background-color 120ms ease",
          "&:hover": { bgcolor: colors.gray50 },
          "&.Mui-expanded": {
            bgcolor: colors.orange50,
            borderLeftColor: colors.accent,
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            bgcolor: colors.gray100,
            transition: "background-color 120ms ease",
          },
          "&:hover .MuiAccordionSummary-expandIconWrapper, &.Mui-expanded .MuiAccordionSummary-expandIconWrapper":
            {
              bgcolor: colors.accent,
              "& svg": { color: colors.white },
            },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, color: colors.primary_dark }}
            >
              {feature.name}
            </Typography>
            {isNew && (
              <Chip
                label={t("new")}
                size="small"
                sx={{
                  bgcolor: colors.accent,
                  color: colors.white,
                  fontWeight: 700,
                }}
              />
            )}
            {feature.audience !== "user" && (
              <Chip
                label={t(`audience.${feature.audience}`)}
                size="small"
                sx={
                  feature.audience === "admin"
                    ? {
                        bgcolor: colors.blue50,
                        color: colors.blue700,
                        border: `1px solid ${colors.blue200}`,
                        fontWeight: 600,
                      }
                    : {
                        bgcolor: colors.gray100,
                        color: colors.gray700,
                        border: `1px solid ${colors.gray300}`,
                      }
                }
              />
            )}
            {feature.status !== "active" && (
              <Chip
                label={t(`status.${feature.status}`)}
                size="small"
                variant="outlined"
                color="warning"
              />
            )}
            {(feature.feature_flags ?? []).map((flag) => (
              <Chip key={flag} label={flag} size="small" variant="outlined" />
            ))}
          </Stack>
          {feature.user.description && (
            <Typography sx={{ fontSize: 13, color: colors.gray600, mt: 0.5 }}>
              {feature.user.description}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      {technical && (
        <AccordionDetails
          sx={{
            bgcolor: colors.gray50,
            borderTop: `2px solid ${colors.gray200}`,
            px: 2,
            py: 1.75,
          }}
        >
          <Stack spacing={1.25}>
            <Typography sx={{ fontSize: 13, color: colors.black }}>
              {technical.description}
            </Typography>
            <AnchorList label={t("frontend")} items={technical.frontend} />
            <AnchorList label={t("backend")} items={technical.backend} />
            {!!technical.caveats?.length && (
              <Box>
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: colors.red700 }}
                >
                  {t("caveats")}
                </Typography>
                {technical.caveats.map((caveat) => (
                  <Typography
                    key={caveat}
                    sx={{ fontSize: 12, color: colors.gray700 }}
                  >
                    • {caveat}
                  </Typography>
                ))}
              </Box>
            )}
            <Typography sx={{ fontSize: 11, color: colors.gray500 }}>
              {feature.id} · {t("updated")} {feature.updated}
              {feature.changed_by?.length
                ? ` · ${feature.changed_by.join(", ")}`
                : ""}
            </Typography>
          </Stack>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

const ReleaseNotesList = ({ notes }: { notes: CatalogReleaseNote[] }) => {
  const { t } = useTranslation("featureCatalog");

  if (!notes.length) {
    return (
      <Typography sx={{ color: colors.gray500 }}>
        {t("noReleaseNotes")}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {notes.map((note) => (
        <Paper
          key={note.pr}
          elevation={0}
          sx={{
            p: 2,
            bgcolor: colors.white,
            border: `1px solid ${colors.gray300}`,
            borderLeft: `4px solid ${CHANGE_TYPE_COLOR[note.change_type] ?? colors.gray300}`,
            borderRadius: "10px",
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography sx={{ fontSize: 12, color: colors.gray500 }}>
              {note.date}
            </Typography>
            <Chip
              label={t(`changeType.${note.change_type}`)}
              size="small"
              sx={{
                fontWeight: 700,
                color: colors.white,
                bgcolor: CHANGE_TYPE_COLOR[note.change_type] ?? colors.gray500,
              }}
            />
            <Chip
              label={note.category}
              size="small"
              variant="outlined"
              sx={{ borderColor: colors.gray300, color: colors.gray700 }}
            />
            {note.audience !== "user" && (
              <Chip
                label={t(`noteAudience.${note.audience}`)}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.primary_dark,
              mt: 0.75,
            }}
          >
            {note.title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.gray700 }}>
            {note.description}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
};

const FeatureCatalog = () => {
  const { t } = useTranslation("featureCatalog");
  const location = useLocation();
  const { data, isLoading } = useFeatureCatalog();
  // The backend already trimmed the catalog for this user; the view only
  // decides which filters make sense.
  const view = data?.view ?? "user";
  const [tab, setTab] = useState<"features" | "releaseNotes">(
    location.state?.tab === "releaseNotes" ? "releaseNotes" : "features",
  );
  const [search, setSearch] = useState("");
  const [moduleId, setModuleId] = useState("all");

  const moduleOptions: ComoSelectOption[] = [
    { id: "all", label: t("allModules") },
    ...(data?.catalog.modules ?? []).map((module) => ({
      id: module.id,
      label: module.title,
    })),
  ];
  const selectedModule = moduleOptions.find((option) => option.id === moduleId);
  const handleModuleChange = (option: ComoSelectOption) => {
    setModuleId(String(option.id));
  };
  const [audience, setAudience] = useState<AudienceFilter>("all");
  const [onlyNew, setOnlyNew] = useState(false);

  const releaseNotes = useMemo(() => data?.release_notes ?? [], [data]);
  const recentIds = useMemo(() => newFeatureIds(releaseNotes), [releaseNotes]);

  const modules = useMemo(
    () =>
      (data?.catalog.modules ?? [])
        .filter((module) => moduleId === "all" || module.id === moduleId)
        .map((module) => ({
          ...module,
          features: module.features.filter(
            (feature) =>
              (audience === "all" || feature.audience === audience) &&
              (!onlyNew || recentIds.has(feature.id)) &&
              matchesSearch(feature, search),
          ),
        }))
        .filter((module) => module.features.length > 0),
    [data, moduleId, audience, onlyNew, recentIds, search],
  );

  const total =
    data?.catalog.modules.reduce((sum, m) => sum + m.features.length, 0) ?? 0;
  const shown = modules.reduce((sum, m) => sum + m.features.length, 0);

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray100 }}
    >
      <Box sx={{ maxWidth: "1280px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Typography
          sx={{ fontSize: 22, fontWeight: 700, color: colors.primary_dark }}
        >
          {t("title")}
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.gray500, mb: 2 }}>
          {!data
            ? t("subtitleLoading")
            : data.catalog.verified_against
              ? t("subtitle", {
                  updated: data.catalog.updated,
                  commit: data.catalog.verified_against.commit,
                })
              : t("subtitleUser", { updated: data.catalog.updated })}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            display: "inline-block",
            mb: 2.5,
            p: 0.5,
            bgcolor: colors.white,
            border: `1px solid ${colors.gray300}`,
            borderRadius: "12px",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTabs-flexContainer": { gap: 0.5 },
              "& .MuiTab-root": {
                minHeight: 0,
                py: 1,
                px: 2,
                borderRadius: "9px",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 600,
                color: colors.gray600,
                transition: "background-color 120ms ease, color 120ms ease",
                "&:hover": { bgcolor: colors.gray100, color: colors.gray900 },
              },
              "& .Mui-selected": {
                bgcolor: `${colors.primary_dark} !important`,
                color: `${colors.white} !important`,
                fontWeight: 700,
              },
            }}
          >
            <Tab
              value="features"
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t("tabFeatures")}</span>
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      borderRadius: "999px",
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor:
                        tab === "features" ? colors.accent : colors.gray200,
                      color: tab === "features" ? colors.white : colors.gray700,
                    }}
                  >
                    {total}
                  </Box>
                </Stack>
              }
            />
            <Tab
              value="releaseNotes"
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t("tabReleaseNotes")}</span>
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      borderRadius: "999px",
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor:
                        tab === "releaseNotes" ? colors.accent : colors.gray200,
                      color:
                        tab === "releaseNotes" ? colors.white : colors.gray700,
                    }}
                  >
                    {releaseNotes.length}
                  </Box>
                </Stack>
              }
            />
          </Tabs>
        </Paper>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : tab === "releaseNotes" ? (
          <ReleaseNotesList notes={releaseNotes} />
        ) : (
          <>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ md: "center" }}
              flexWrap="wrap"
              useFlexGap
              mb={2}
            >
              <TextField
                size="small"
                placeholder={t("searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 240,
                  bgcolor: colors.white,
                  borderRadius: "8px",
                  "& .MuiInputBase-input": {
                    color: colors.gray900,
                    fontSize: 15,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.gray300,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <ComoSelect
                options={moduleOptions}
                selectedOption={selectedModule}
                onSelect={handleModuleChange}
                placeholder={t("allModules")}
                sx={{ minWidth: 240, mb: 0 }}
              />
              {/* A user only gets user features, so there is nothing to filter. */}
              {view !== "user" && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={audience}
                  onChange={(_, value) => value && setAudience(value)}
                  sx={{
                    bgcolor: colors.white,
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      color: colors.gray600,
                      borderColor: colors.gray300,
                      "&:hover": { bgcolor: colors.gray100 },
                      "&.Mui-selected": {
                        bgcolor: colors.primary_dark,
                        color: colors.white,
                        "&:hover": { bgcolor: colors.secondary },
                      },
                    },
                  }}
                >
                  <ToggleButton value="all">{t("audience.all")}</ToggleButton>
                  <ToggleButton value="user">{t("audience.user")}</ToggleButton>
                  <ToggleButton value="admin">
                    {t("audience.admin")}
                  </ToggleButton>
                  {view === "admin" && (
                    <ToggleButton value="system">
                      {t("audience.system")}
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              )}
              <ToggleButton
                size="small"
                value="new"
                selected={onlyNew}
                onChange={() => setOnlyNew(!onlyNew)}
                sx={{
                  bgcolor: colors.white,
                  textTransform: "none",
                  fontWeight: 600,
                  color: colors.gray600,
                  borderColor: colors.gray300,
                  "&:hover": { bgcolor: colors.gray100 },
                  "&.Mui-selected": {
                    bgcolor: colors.accent,
                    color: colors.white,
                    borderColor: colors.accent,
                    "&:hover": { bgcolor: colors.accent_dark },
                  },
                }}
              >
                {t("onlyNew")}
              </ToggleButton>
            </Stack>

            <Typography sx={{ fontSize: 12, color: colors.gray500, mb: 1.5 }}>
              {t("count", { shown, total })}
            </Typography>

            <Stack spacing={3}>
              {modules.map((module) => (
                <Box key={module.id}>
                  <Box
                    sx={{
                      borderLeft: `4px solid ${colors.accent}`,
                      pl: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography
                        sx={{
                          fontSize: 17,
                          fontWeight: 700,
                          color: colors.primary_dark,
                        }}
                      >
                        {module.order}. {module.title}
                      </Typography>
                      <Chip
                        label={module.features.length}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: colors.primary_dark,
                          color: colors.white,
                        }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 13, color: colors.gray600 }}>
                      {module.user_summary ?? module.summary}
                    </Typography>
                  </Box>
                  {module.features.map((feature) => (
                    <FeatureRow
                      key={feature.id}
                      feature={feature}
                      isNew={recentIds.has(feature.id)}
                    />
                  ))}
                </Box>
              ))}
              {!modules.length && (
                <Typography sx={{ color: colors.gray500 }}>
                  {t("noResults")}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};

export default FeatureCatalog;
